import { useState, useEffect } from "react";
import { Form, Input, Button, Table, Card, Typography, Select, message, Row, Col, Spin } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { Option } = Select;

const RawMaterialRequest = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [notesRes, requestsRes] = await Promise.all([
          axios.get("https://agpipefitting.org.et/api/receiving-notes"),
          axios.get("https://agpipefitting.org.et/api/material-requests")
        ]);

        // Process receiving notes to calculate stock and extract items
        const stockMap = {};
        const itemsMap = {};

        notesRes.data.forEach(note => {
          note.items.forEach(item => {
            // Aggregate stock quantities
            stockMap[item.item_code] = (stockMap[item.item_code] || 0) + Number(item.quantity);
            
            // Store item details from first occurrence
            if (!itemsMap[item.item_code]) {
              itemsMap[item.item_code] = {
                code: item.item_code,
                name: item.item_name,
                unit: item.unit
              };
            }
          });
        });

        // Merge stock data with item details
        const processedItems = Object.keys(itemsMap).map(code => ({
          ...itemsMap[code],
          stock: stockMap[code] || 0
        }));

        setItemsList(processedItems);
        setRequests(requestsRes.data);
      } catch (error) {
        message.error("Failed to load initial data");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleItemChange = (value, fieldName) => {
    const selectedItem = itemsList.find(item => item.code === value);
    form.setFieldsValue({
      [fieldName]: {
        unit: selectedItem?.unit || ''
      }
    });
  };

  const validateQuantity = (name) => (_, value) => {
    const itemCode = form.getFieldValue(['items', name, 'item_code']);
    const selectedItem = itemsList.find(item => item.code === itemCode);
    
    if (value > (selectedItem?.stock || 0)) {
      return Promise.reject('Quantity exceeds available stock');
    }
    if (value <= 0) {
      return Promise.reject('Quantity must be positive');
    }
    return Promise.resolve();
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const requestData = {
        ...values,
        status: "pending",
        requested_date: new Date().toISOString(),
        items: values.items.map(item => ({
          ...item,
          quantity: Number(item.quantity),
        }))
      };

      await axios.post("https://agpipefitting.org.et/api/material-requests", requestData);
      message.success("Request submitted successfully");
      form.resetFields();
      setRequests(prev => [...prev, requestData]);
    } catch (error) {
      message.error(error.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Requisition Number", dataIndex: "requisition_number", width: 200 },
    { title: "Department", dataIndex: "department", width: 180 },
    { title: "Purpose", dataIndex: "purpose", ellipsis: true },
    { 
      title: "Status", 
      dataIndex: "status", 
      render: status => (
        <span className={`status-badge status-${status}`}>
          {status.toUpperCase()}
        </span>
      ),
      width: 120
    },
    { 
      title: "Requested Date", 
      dataIndex: "requested_date",
      render: date => new Date(date).toLocaleDateString(),
      width: 150
    },
  ];

  if (initialLoading) {
    return <Spin size="large" className="loading-spinner" />;
  }

  return (
    <div className="container">
      <Title level={3} className="page-title">Raw Material Requisition System</Title>
      
      <Card title="New Material Request" className="form-card">
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item 
                label="Requisition Number"
                name="requisition_number"
                rules={[{ required: true, message: 'Please input requisition number' }]}
              >
                <Input placeholder="REQ-2023-001" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item 
                label="Department"
                name="department"
                rules={[{ required: true, message: 'Please input department' }]}
              >
                <Input placeholder="Production Line 1" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            label="Purpose of Request"
            name="purpose"
            rules={[{ required: true, message: 'Please specify purpose' }]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Describe the purpose of this material request in detail"
              showCount 
              maxLength={500}
            />
          </Form.Item>

          <Title level={5} className="section-title">Requested Items</Title>
          
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} className="item-row">
                    <Row gutter={16} align="middle">
                      <Col xs={24} sm={10}>
                        <Form.Item
                          {...restField}
                          name={[name, "item_code"]}
                          rules={[{ required: true, message: 'Item required' }]}
                        >
                          <Select
                            showSearch
                            placeholder="Select Item"
                            optionFilterProp="children"
                            onChange={(value) => handleItemChange(value, ['items', name])}
                            filterOption={(input, option) =>
                              option.children[0].toLowerCase().includes(input.toLowerCase())
                            }
                            className="item-select"
                          >
                            {itemsList.map(item => (
                              <Option 
                                key={item.code} 
                                value={item.code}
                                disabled={item.stock <= 0}
                              >
                                <div className="item-option">
                                  <Text strong>{item.name}</Text>
                                  <div className="item-stock-info">
                                    <Text type="secondary">{item.unit}</Text>
                                    <Text type={item.stock > 0 ? "success" : "danger"}>
                                      {item.stock} available
                                    </Text>
                                  </div>
                                </div>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      
                      <Col xs={12} sm={5}>
                        <Form.Item
                          {...restField}
                          name={[name, "quantity"]}
                          rules={[
                            { required: true, message: 'Quantity required' },
                            { validator: validateQuantity(name) }
                          ]}
                        >
                          <Input 
                            type="number" 
                            placeholder="Qty" 
                            min={1}
                            className="quantity-input"
                          />
                        </Form.Item>
                      </Col>
                      
                      <Col xs={10} sm={5}>
                        <Form.Item
                          {...restField}
                          name={[name, "unit"]}
                        >
                          <Input placeholder="Unit" disabled className="unit-input" />
                        </Form.Item>
                      </Col>
                      
                      <Col xs={2} sm={4}>
                        <Button
                          danger
                          icon={<MinusOutlined />}
                          onClick={() => remove(name)}
                          className="remove-btn"
                        />
                      </Col>
                    </Row>
                  </div>
                ))}
                
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    className="add-item-btn"
                  >
                    Add Item
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div className="form-actions">
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="submit-btn"
            >
              Submit Request
            </Button>
            <Button 
              htmlType="button" 
              onClick={() => form.resetFields()}
              className="reset-btn"
            >
              Clear Form
            </Button>
          </div>
        </Form>
      </Card>

      <Card title="Request History" className="history-card">
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="requisition_number"
          pagination={{ pageSize: 5 }}
          scroll={{ x: true }}
          className="request-table"
          bordered
        />
      </Card>

      <style jsx global>{`
        .container {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .page-title {
          margin-bottom: 32px !important;
          color: #1a3353;
        }
        
        .form-card, .history-card {
          margin-bottom: 32px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .section-title {
          margin: 24px 0 16px !important;
          color: #1a3353;
        }
        
        .item-row {
          margin-bottom: 16px;
          padding: 12px;
          background: #fafafa;
          border-radius: 4px;
        }
        
        .item-select .ant-select-selector {
          height: 40px !important;
        }
        
        .item-option {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        
        .item-stock-info {
          display: flex;
          gap: 8px;
          margin-left: 12px;
        }
        
        .quantity-input, .unit-input {
          width: 100%;
        }
        
        .remove-btn {
          width: 100%;
          height: 40px;
        }
        
        .add-item-btn {
          height: 40px;
          border-color: #1890ff;
          color: #1890ff;
        }
        
        .form-actions {
          display: flex;
          gap: 16px;
          margin-top: 24px;
        }
        
        .submit-btn {
          height: 40px;
          padding: 0 32px;
        }
        
        .reset-btn {
          height: 40px;
          border-color: #ff4d4f;
          color: #ff4d4f;
        }
        
        .request-table {
          margin-top: 16px;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .status-pending {
          background: #fffbe6;
          color: #faad14;
        }
        
        .status-approved {
          background: #f6ffed;
          color: #52c41a;
        }
        
        .status-rejected {
          background: #fff1f0;
          color: #ff4d4f;
        }
        
        .loading-spinner {
          display: flex !important;
          justify-content: center;
          margin-top: 100px;
        }
      `}</style>
    </div>
  );
};

export default RawMaterialRequest;